import React, { useState, useEffect, useMemo } from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import {
  useAreaList,
  useSubAreaList,
} from "@features/mx/readCycleManagement/hooks";

interface PremiseFilters {
  area: string;
  subarea: string;
}

interface PremiseFiltersComponentProps {
  area: string;
  subarea: string;
  onApplyFilters: (filters: PremiseFilters) => void;
  onResetFilters: () => void;
}

export const PremiseFiltersComponent: React.FC<
  PremiseFiltersComponentProps
> = ({ area, subarea, onApplyFilters, onResetFilters }) => {
  const [areaValue, setAreaValue] = useState<string>("");
  const { data: areaList } = useAreaList();
  const { data: subareaList } = useSubAreaList(areaValue);

  const areas = useMemo(
    () =>
      areaList?.map((area: any) => ({
        value: area.id,
        label: area.name,
      })),
    [areaList]
  );
  const subareas = useMemo(
    () =>
      subareaList?.map((subarea: any) => ({
        value: subarea.id,
        label: subarea.name,
      })),
    [subareaList]
  );

  // Define filter fields similar to PremisesTab
  const filterFields: FormField[] = useMemo(
    () => [
      {
        name: "area",
        label: "Area",
        type: "select",
        placeholder: "Select Area",
        options: areas,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
      {
        name: "subarea",
        label: "Subarea",
        type: "select",
        placeholder: "Select Subarea",
        options: subareas,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
    ],
    [areas, subareas]
  );

  // Create form instance
  const form = FormService.createForm({
    fields: filterFields,
    mode: "onChange",
    validateOnMount: false,
  });

  // Sync form values when URL params change
  useEffect(() => {
    form.reset({
      area: decodeURIComponent(area) || "",
      subarea: decodeURIComponent(subarea) || "",
    });
  }, [area, subarea, form]);

  const handleFilterSubmit = () => {
    const formValues = form.getValues();
    onApplyFilters({
      area: formValues.area || "",
      subarea: formValues.subarea || "",
    });
  };

  const handleFieldChange = (field: string, value: string) => {
    switch (field) {
      case "area":
        setAreaValue(value);
        break;
      default:
        break;
    }
  };

  const handleFilterReset = () => {
    form.reset({
      area: "",
      subarea: "",
    });
    onResetFilters();
  };

  return (
    <div>
      <DynamicForm
        fields={filterFields}
        form={form}
        onSubmit={handleFilterSubmit}
        onReset={handleFilterReset}
        onFieldChange={handleFieldChange}
        gridColumns={2}
        config={{
          layout: "vertical",
          showSubmitButton: true,
          showResetButton: true,
          submitButtonText: "Apply Filters",
          resetButtonText: "Clear",
          classes: {
            buttonContainer: "flex justify-end gap-4",
          },
        }}
      />
    </div>
  );
};
